import type { Suggestion } from '../../shared/types';
import Eyebrow from './Eyebrow';
import Button from './Button';

interface Props {
  enabled: boolean;
  suggestion: Suggestion | null;
  onStartCompare: () => void;
}

export default function SuggestionPanel({ enabled, suggestion, onStartCompare }: Props) {
  if (!enabled) return null;

  // Idle / no pending suggestion
  if (!suggestion || suggestion.status === 'editing') {
    return (
      <section className="bg-card border border-line rounded-md p-5 md:p-6">
        <Eyebrow accent>// Suggested edits</Eyebrow>
        <p className="text-faint text-[13px] leading-[1.55] mt-3">
          {suggestion?.status === 'editing'
            ? 'Client is currently drafting suggestions. They’ll appear here when submitted.'
            : 'No pending suggestions. The client will see a "Suggest edits" button on their review page.'}
        </p>
      </section>
    );
  }

  // Submitted — awaiting review
  const submittedAgo = suggestion.submitted_at ? timeAgo(suggestion.submitted_at) : 'recently';
  const author = suggestion.author_name || 'Client';

  return (
    <section className="bg-card border border-accent rounded-md p-5 md:p-6">
      <div className="flex items-start justify-between gap-3 mb-3">
        <Eyebrow accent>// Suggested edits · pending</Eyebrow>
        <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
          v{suggestion.based_on_version}
        </span>
      </div>
      <p className="text-fg text-[13px] mb-2">
        <span className="font-medium">{author}</span>{' '}
        <span className="text-faint">submitted {submittedAgo}</span>
      </p>
      {suggestion.note && (
        <blockquote className="border-l-2 border-accent pl-3 text-[13px] text-muted leading-[1.55] my-3 italic">
          {suggestion.note}
        </blockquote>
      )}
      <Button variant="primary" size="sm" glow onClick={onStartCompare}>
        Compare side-by-side
      </Button>
    </section>
  );
}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
